/**
 * Mock data for the Product Catalogue journeys.
 *
 * A realistic Core range plus two customer catalogues (Ramsay, SJP)
 * covering every field mode, multi-part items, all three fulfilment types, and
 * the artwork modes. Specs/quantities echo the standardisation analysis
 * (docs/features/catalogue-standardisation/). Locally typed for journey
 * iteration; productionised to the DataAdapter + codegen later.
 */

import { ApiError } from "../data/api/errors";
import {
  mockPriceLevels,
  mockPrecedentForCataloguePreview,
} from "./price-precedent";
import type {
  Catalogue,
  CatalogueCostRefreshRun,
  CatalogueDelivery,
  CatalogueDeliveryOption,
  CatalogueDeliverySelection,
  CatalogueItem,
  CatalogueItemPricePreview,
  CatalogueItemTile,
  CatalogueItemVariantWithCosts,
  CatalogueOption,
  CataloguePriceLadderRow,
  CatalogueView,
  DictionaryWorkbookScope,
  PriceProvenance,
  VariantQuantityCost,
} from "../data/catalogue-types";
import type { CatalogueItemListOptions, ListResponse } from "../data/adapter";
import type { QuoteLine } from "../data/pricing-quote";
import type { SpeedChoice } from "../data/speed-types";
import {
  mockInHouseOptions,
  mockSpeedChoice,
  type MockSpeedRequest,
} from "./speeds";
import { getMockAssignments } from "./pricing-assignments";
import { priceForQuantity } from "../domain/catalogue-pricing";
import { needsSetup } from "../domain/catalogue-presentation";
import {
  BUSINESS_CARD_BOARDS,
  BUSINESS_CARD_DEFAULT_BOARD,
  LEAFLET_DEFAULT_PAPER,
  LEAFLET_PAPER_STOCKS,
} from "./dictionary-paper-fields";
import { COSTED_AT, getMockPriceCosting } from "./cost-engine-validation";
import {
  formatCostDrilldownLine,
  type CostDrilldownPress,
} from "../domain/cost-engine-validation-presentation";

// ── Delivery, as price_delivery answers it (order to invoice doc 104 §5) ──
//
// The courier options the mock prices every shipped delivery from: the
// mock-up's list (a 38% markup on our carriage cost), the cheapest non-timed
// option the default, and a pallet level no courier prices. Three spine
// addresses read otherwise: Truro (BRAM's "…a003") is not yet priced because
// the item has no weight or packing, until a weight and parcels are entered;
// Dublin ("…a004") is covered by no courier.
const MOCK_DELIVERY_MARKUP_PCT = 38;
const MOCK_DELIVERY_OPTIONS: readonly CatalogueDeliveryOption[] = [
  {
    serviceLevel: "next_day",
    serviceLevelLabel: "Next day",
    courierName: "DPD",
    courierSupplierId: "sup-dpd",
    carriageCostPence: 710,
    deliveryPricePence: 980,
    isAvailable: true,
    unavailableReason: null,
    arrivesOn: "2026-10-15",
    isDefault: true,
  },
  {
    serviceLevel: "next_day",
    serviceLevelLabel: "Next day",
    courierName: "DX",
    courierSupplierId: "sup-dx",
    carriageCostPence: 840,
    deliveryPricePence: 1159,
    isAvailable: true,
    unavailableReason: null,
    arrivesOn: "2026-10-15",
    isDefault: false,
  },
  {
    serviceLevel: "pre_noon",
    serviceLevelLabel: "Before noon",
    courierName: "DPD",
    courierSupplierId: "sup-dpd",
    carriageCostPence: 1060,
    deliveryPricePence: 1463,
    isAvailable: true,
    unavailableReason: null,
    arrivesOn: "2026-10-15",
    isDefault: false,
  },
  {
    serviceLevel: "pre_1030",
    serviceLevelLabel: "Before 10:30",
    courierName: "DX",
    courierSupplierId: "sup-dx",
    carriageCostPence: 1630,
    deliveryPricePence: 2249,
    isAvailable: true,
    unavailableReason: null,
    arrivesOn: "2026-10-15",
    isDefault: false,
  },
  {
    serviceLevel: "pallet",
    serviceLevelLabel: "Pallet",
    courierName: "DPD",
    courierSupplierId: "sup-dpd",
    carriageCostPence: null,
    deliveryPricePence: null,
    isAvailable: false,
    unavailableReason: "Pallets aren't priced",
    arrivesOn: null,
    isDefault: false,
  },
];
const MOCK_NO_PROFILE_ADDRESS_ID = "5b1f0c6e-2d4a-4b8e-9c31-7e2a40d1a003";
export const MOCK_NO_COURIER_ADDRESS_ID =
  "5b1f0c6e-2d4a-4b8e-9c31-7e2a40d1a004";

const UNPRICED: Omit<CatalogueDelivery, "status"> = {
  deliveryPricePence: null,
  source: null,
  serviceLevel: null,
  courierName: null,
  courierSupplierId: null,
  arrivesOn: null,
  notPricedMessage: null,
  notPricedReason: null,
  carriageCostPence: null,
  markupPct: null,
  options: [],
};

/**
 * A split line's addresses, each priced alone at its default service, as the
 * catalogue's item-page preview does (order to invoice doc 106 §3.4); empty
 * for a line going to one place.
 */
export function mockPriceDestinations(
  itemId: string,
  delivery: CatalogueDeliverySelection | null | undefined,
): CatalogueDelivery[] {
  const rows = (delivery?.deliveryLines ?? []).filter(
    (row) => row.deliveryAddressId != null && row.quantity > 0,
  );
  if (delivery?.mode !== "multi" || rows.length === 0) return [];
  return rows.map((row) =>
    mockPriceDelivery(itemId, {
      ...delivery,
      mode: "ship",
      deliveryAddressId: row.deliveryAddressId,
      serviceLevel: null,
      courierSupplierId: null,
      agreedDeliveryPricePence: null,
      agreedDeliveryReason: null,
      weightKg: null,
      parcels: null,
      deliveryLines: [],
    }),
  );
}

/** A split line's addresses as one figure, as the catalogue sums them: not
 *  yet priced while any is, never a part-sum; no courier list. */
function mockCombinedDelivery(each: CatalogueDelivery[]): CatalogueDelivery {
  if (each.every((d) => d.status === "included"))
    return { ...UNPRICED, status: "included" };
  const pending = each.find((d) => d.status === "not_yet_priced");
  if (pending)
    return {
      ...UNPRICED,
      status: "not_yet_priced",
      notPricedMessage: pending.notPricedMessage,
      notPricedReason: pending.notPricedReason,
    };
  return {
    ...UNPRICED,
    status: "priced",
    source: "courier_quote",
    deliveryPricePence: each.reduce(
      (sum, d) => sum + (d.deliveryPricePence ?? 0),
      0,
    ),
  };
}

/**
 * The delivery on a mock price preview, mirroring the catalogue's
 * `price_delivery`: collected is £0; shipped with no address is
 * `address_required`; an agreed price (with its reason) is the price, no
 * markup; otherwise the chosen (or default) courier option, or not yet priced
 * with the server's sentence. Shared with the supplier-pricing mock.
 */
export function mockPriceDelivery(
  itemId: string,
  delivery: CatalogueDeliverySelection | null | undefined,
): CatalogueDelivery {
  const each = mockPriceDestinations(itemId, delivery);
  if (each.length > 0) return mockCombinedDelivery(each);
  if (delivery?.mode === "collect")
    return {
      ...UNPRICED,
      status: "collection",
      deliveryPricePence: 0,
      serviceLevel: "collection",
    };
  if (
    (delivery?.mode !== "ship" && delivery?.mode !== "multi") ||
    !delivery.deliveryAddressId
  )
    return { ...UNPRICED, status: "address_required" };
  const agreedReason = delivery.agreedDeliveryReason?.trim();
  if (delivery.agreedDeliveryPricePence != null && agreedReason)
    return {
      ...UNPRICED,
      status: "priced",
      deliveryPricePence: delivery.agreedDeliveryPricePence,
      source: "agreed",
      markupPct: 0,
      options: [...MOCK_DELIVERY_OPTIONS],
    };
  if (delivery.deliveryAddressId === MOCK_NO_COURIER_ADDRESS_ID)
    return {
      ...UNPRICED,
      status: "not_yet_priced",
      notPricedMessage:
        "Delivery isn't priced: no courier covers this address yet. Enter an agreed price.",
      notPricedReason: "no_courier_options",
    };
  const hasPackages = delivery.weightKg != null && delivery.parcels != null;
  if (delivery.deliveryAddressId === MOCK_NO_PROFILE_ADDRESS_ID && !hasPackages)
    return {
      ...UNPRICED,
      status: "not_yet_priced",
      notPricedMessage: `Delivery isn't priced: ${getMockCatalogueItem(itemId)?.name ?? "An item"} has no weight or packing. Enter its weight and parcels, or an agreed price.`,
      notPricedReason: "profile_missing",
    };
  const chosen = delivery.serviceLevel
    ? MOCK_DELIVERY_OPTIONS.find(
        (o) =>
          o.serviceLevel === delivery.serviceLevel &&
          (delivery.courierSupplierId == null ||
            o.courierSupplierId === delivery.courierSupplierId),
      )
    : MOCK_DELIVERY_OPTIONS.find((o) => o.isDefault);
  if (!chosen || !chosen.isAvailable || chosen.deliveryPricePence == null)
    return {
      ...UNPRICED,
      status: "not_yet_priced",
      notPricedMessage:
        "Delivery isn't priced: that service isn't offered to this address. Choose another.",
      notPricedReason: "level_not_available",
      options: [...MOCK_DELIVERY_OPTIONS],
    };
  return {
    status: "priced",
    deliveryPricePence: chosen.deliveryPricePence,
    source: "courier_quote",
    serviceLevel: chosen.serviceLevel,
    courierName: chosen.courierName,
    courierSupplierId: chosen.courierSupplierId,
    arrivesOn: chosen.arrivesOn,
    notPricedMessage: null,
    notPricedReason: null,
    carriageCostPence: chosen.carriageCostPence,
    markupPct: MOCK_DELIVERY_MARKUP_PCT,
    options: [...MOCK_DELIVERY_OPTIONS],
  };
}

/** The print price with its delivery, or null when the delivery has no price. */
export function mockTotalWithDelivery(
  quotedPence: number,
  delivery: CatalogueDelivery,
): number | null {
  return delivery.deliveryPricePence == null
    ? null
    : quotedPence + delivery.deliveryPricePence;
}

function o(
  code: string,
  label: string,
  submit?: string,
  isDefault?: boolean,
  priceDelta?: number,
): CatalogueOption {
  return { code, label, submit, isDefault, priceDelta };
}

// ── Catalogues (scopes) ─────────────────────────────────────────────────────

export const mockCatalogues: Catalogue[] = [
  {
    id: "core-range",
    name: "Core range",
    audience: "core_range",
    accountIds: [],
    ownedBy: "code",
  },
  {
    id: "ramsay",
    name: "Ramsay Healthcare",
    ownedBy: "code",
    audience: "allocated",
    privateTo: { customerCode: "RAM1379", customerName: "Ramsay Healthcare" },
    accountIds: ["cust-ramsay"],
    // Pinned to in-house (#87): Ramsay's house board is made by us, so no
    // supplier route is offered on anything in this catalogue.
    routeNarrowing: { allowedRoutes: ["in_house"], pinnedRoute: "in_house" },
  },
  {
    id: "perivan-sjp",
    name: "SJP — online stationery",
    audience: "allocated",
    privateTo: { customerCode: "PER01", customerName: "St James's Place" },
    accountIds: ["cust-sjp"],
    ownedBy: "db",
  },
];

const seededOwners = new Map(mockCatalogues.map((c) => [c.id, c.ownedBy]));

/** *Hand over to One…* in the mock: the catalogue is managed in One from now on (this session only). */
export function handOverMockCatalogue(catalogueId: string): void {
  const catalogue = mockCatalogues.find((c) => c.id === catalogueId);
  if (catalogue) catalogue.ownedBy = "db";
}

/** Put every mock catalogue's ownership back as authored (tests and stories that hand one over). */
export function resetMockCatalogueOwners(): void {
  for (const catalogue of mockCatalogues)
    catalogue.ownedBy = seededOwners.get(catalogue.id) ?? catalogue.ownedBy;
}

// ── Core range items ────────────────────────────────────────────────

const standardItems: CatalogueItem[] = [
  {
    highlights: [],
    relatedItemIds: [],
    id: "standard-business-card",
    vatTreatment: "standard",
    name: "Business card",
    catalogueId: "core-range",
    parts: ["sheet"],
    productLabel: "Business Cards",
    icon: "id-badge-2",
    defaultQuantity: 250,
    quantityBreaks: [
      { qty: 100, price: 30 },
      { qty: 250, price: 48 },
      { qty: 500, price: 72 },
      { qty: 1000, price: 110 },
      { qty: 2000, price: 180 },
    ],
    artworkModes: ["upload", "online_editor", "later"],
    fields: [
      {
        key: "Size",
        label: "Size",
        mode: "choice_or_custom",
        options: [
          o("85x55", "85 × 55 mm", "55X85", true),
          o("90x55", "90 × 55 mm", "55X90"),
        ],
        customValueType: "dimensions",
        customHint: "Any size within the press bed",
        customConstraints: {
          min_width_mm: 40,
          max_width_mm: 150,
          min_depth_mm: 40,
          max_depth_mm: 150,
        },
      },
      {
        key: "Print",
        label: "Print",
        mode: "choice",
        options: [
          o("4/4", "Full colour, both sides", "4/4", true),
          o("4/0", "One side", "4/0"),
          o("5/5", "Colour + spot", "5/5", false, 6),
        ],
      },
      {
        key: "Board",
        label: "Board",
        mode: "choice",
        options: [
          o("350", "350gsm white", "WWW-UNC-350", true),
          o("400", "400gsm white", "WWW-UNC-400"),
        ],
      },
      {
        key: "Finishing",
        label: "Finishing",
        mode: "choice",
        control: "multi",
        options: [
          o("lam", "Lamination", "Laminating", false, 8),
          o("spotuv", "Spot UV", "Touch Enhancement", false, 12),
          o("round", "Rounded corners", "Cut-Crease-Perf", false, 6),
        ],
      },
      // WP9's count: how many artworks share the spec (R23).
      {
        key: "Versions",
        label: "Versions",
        mode: "always_custom",
        control: "number",
        customValueType: "integer",
        min: 1,
        max: 26,
        step: 1,
        customHint:
          "How many artworks share this spec; the quantity is the total across them",
      },
    ],
  },
  {
    highlights: [],
    relatedItemIds: [],
    id: "standard-letterhead",
    vatTreatment: "standard",
    name: "Letterhead",
    catalogueId: "core-range",
    parts: ["sheet"],
    productLabel: "Letterheads",
    icon: "file-text",
    defaultQuantity: 1000,
    quantityBreaks: [
      { qty: 500, price: 55 },
      { qty: 1000, price: 85 },
      { qty: 2000, price: 140 },
      { qty: 5000, price: 290 },
      { qty: 10000, price: 520 },
    ],
    artworkModes: ["on_file", "upload", "later"],
    fields: [
      {
        key: "Size",
        label: "Size",
        mode: "fixed",
        fixedValue: "A4 (210 × 297 mm)",
      },
      {
        key: "Print",
        label: "Print",
        mode: "choice",
        options: [
          o("4/0", "Full colour, one side", "4/0", true),
          o("4/4", "Both sides", "4/4"),
          o("1/0", "Mono", "1/0"),
          o("2/0", "Two colour", "2/0"),
        ],
      },
      {
        key: "Stock",
        label: "Paper",
        mode: "choice",
        options: [
          o(
            "lhbond",
            "100–120gsm uncoated bond",
            "STATIONERY/UNCOATED/100",
            true,
          ),
        ],
      },
    ],
  },
  {
    highlights: [],
    relatedItemIds: [],
    id: "standard-compliment-slip",
    vatTreatment: "standard",
    name: "Compliment slip",
    catalogueId: "core-range",
    parts: ["sheet"],
    productLabel: "Compliment Slip",
    icon: "note",
    defaultQuantity: 500,
    quantityBreaks: [
      { qty: 250, price: 40 },
      { qty: 500, price: 62 },
      { qty: 1000, price: 95 },
      { qty: 2000, price: 160 },
      { qty: 5000, price: 340 },
    ],
    artworkModes: ["on_file", "upload", "later"],
    fields: [
      {
        key: "Size",
        label: "Size",
        mode: "fixed",
        fixedValue: "DL (210 × 99 mm)",
      },
      {
        key: "Print",
        label: "Print",
        mode: "choice",
        options: [
          o("4/0", "Full colour, one side", "4/0", true),
          o("4/4", "Both sides", "4/4"),
          o("1/0", "Mono", "1/0"),
        ],
      },
      {
        key: "Stock",
        label: "Paper",
        mode: "choice",
        options: [o("bond", "100gsm white bond", "WWW-UNC-100", true)],
      },
    ],
  },
  {
    highlights: [],
    relatedItemIds: [],
    id: "standard-flat-leaflet",
    vatTreatment: "zero_rated",
    name: "Flat leaflet",
    catalogueId: "core-range",
    parts: ["sheet"],
    productLabel: "Leaflets - 2pp",
    icon: "file-text",
    defaultQuantity: 1000,
    quantityBreaks: [
      { qty: 250, price: 45 },
      { qty: 500, price: 60 },
      { qty: 1000, price: 85 },
      { qty: 2500, price: 150 },
      { qty: 5000, price: 240 },
    ],
    artworkModes: ["upload", "later"],
    fields: [
      {
        key: "Size",
        label: "Size",
        mode: "choice",
        options: [
          o("a4", "A4 (210 × 297 mm)", "a4"),
          o("a5", "A5 (148 × 210 mm)", "a5", true),
          o("210x99", "DL (210 × 99 mm)", "210x99"),
        ],
      },
      {
        key: "Print",
        label: "Print",
        mode: "choice",
        options: [
          o("4/4", "Full colour, both sides", "4/4", true),
          o("4/0", "Full colour, one side", "4/0"),
        ],
      },
      {
        // dict-leaflet-2pp's paper as the API sends it: every option a
        // paper, so the item page offers paper type, then weight.
        key: "Stock",
        label: "Paper",
        mode: "choice",
        options: LEAFLET_PAPER_STOCKS.map((s) => ({
          ...o(s.code, s.label, s.code, s.code === LEAFLET_DEFAULT_PAPER),
          paper: s.paper,
        })),
      },
    ],
  },
  {
    highlights: [],
    relatedItemIds: [],
    id: "standard-business-card-every-board",
    vatTreatment: "standard",
    name: "Business card (every board)",
    catalogueId: "core-range",
    parts: ["sheet"],
    productLabel: "Business Cards",
    icon: "id-badge-2",
    defaultQuantity: 250,
    quantityBreaks: [
      { qty: 100, price: 30 },
      { qty: 250, price: 48 },
      { qty: 500, price: 72 },
      { qty: 1000, price: 110 },
    ],
    artworkModes: ["upload", "later"],
    fields: [
      {
        key: "Print",
        label: "Print",
        mode: "choice",
        options: [
          o("4/4", "Full colour, both sides", "4/4", true),
          o("4/0", "One side", "4/0"),
        ],
      },
      {
        // dict-business-card's board: papers by type then weight, and the two
        // named stocks, which carry no `paper`, as types of their own.
        key: "Board",
        label: "Board",
        mode: "choice",
        options: BUSINESS_CARD_BOARDS.map((s) => ({
          ...o(s.code, s.label, s.code, s.code === BUSINESS_CARD_DEFAULT_BOARD),
          paper: s.paper,
        })),
      },
    ],
  },
  {
    highlights: [],
    relatedItemIds: [],
    id: "standard-brochure",
    // UK printed-matter VAT split (doc §1): a brochure is zero-rated.
    vatTreatment: "zero_rated",
    name: "Brochure",
    catalogueId: "core-range",
    // Manufacturing surfaces review ("Read it as Henry" — bridging the mock
    // ID gap): this item's own product type (1 · Brochure Cover & Text)
    // already matches cost-engine-validation.ts's paused
    // "dict-brochure-cover-text" fixture, so it's wired to that dictionary
    // item rather than falling back to its own id — giving the catalogue
    // item page's route/price provenance a real, paused item to render for.
    derivesFromDictionaryItemId: "dict-brochure-cover-text",
    parts: ["cover", "text"],
    productLabel: "Brochures — Cover and Text",
    icon: "book",
    defaultQuantity: 100,
    quantityBreaks: [
      { qty: 50, price: 180 },
      { qty: 100, price: 260 },
      { qty: 250, price: 480 },
      { qty: 500, price: 760 },
      { qty: 1000, price: 1180 },
      { qty: 2500, price: 2400 },
    ],
    artworkModes: ["upload", "later"],
    fields: [
      {
        key: "Size",
        label: "Size",
        mode: "choice",
        options: [o("a4", "A4", "A4", true), o("a5", "A5", "A5")],
      },
      {
        key: "Pages",
        label: "Pages",
        mode: "choice_or_custom",
        // Number control, matching the real seed (catalogue_module/seed.py) —
        // a compact +/- stepper, not chips. The dozen extents that make up
        // ~90% of brochure revenue — the rest come in as a custom value
        // (design doc "why this is the pivotal phase").
        control: "number",
        defaultValue: "8",
        options: [
          o("4", "4pp"),
          o("8", "8pp", undefined, true),
          o("12", "12pp"),
          o("16", "16pp"),
          o("24", "24pp"),
          o("32", "32pp"),
        ],
        customValueType: "integer",
        step: 4,
        min: 4,
        max: 48,
        customHint: "Other page count, in multiples of 4 (4–48)",
      },
      {
        key: "Binding",
        label: "Binding",
        mode: "fixed",
        fixedValue: "Saddle-stitched",
      },
      {
        key: "CoverPrint",
        label: "Print",
        section: "Cover",
        mode: "choice",
        options: [
          o("4/4", "Full colour, both sides", "4/4", true),
          o("5/5", "Colour + spot", "5/5", false, 40),
        ],
      },
      {
        key: "CoverBoard",
        label: "Board",
        section: "Cover",
        mode: "choice",
        options: [o("silk", "Silk/matt 250–350gsm", "WWW-SI-300", true)],
      },
      {
        key: "TextPrint",
        label: "Print",
        section: "Text",
        mode: "choice",
        options: [
          o("4/4", "Full colour", "4/4", true),
          o("5/5", "Colour + spot", "5/5", false, 40),
          o("1/1", "Mono", "1/1"),
        ],
      },
      {
        key: "TextStock",
        label: "Stock",
        section: "Text",
        mode: "choice",
        options: [o("silk", "Silk/matt 100–170gsm", "WWW-SI-150", true)],
      },
      {
        key: "Finishing",
        label: "Finishing",
        mode: "choice",
        control: "multi",
        options: [o("lam", "Lamination", "Laminating", false, 40)],
      },
    ],
  },
  {
    highlights: [],
    relatedItemIds: [],
    id: "standard-folder",
    // Deliberately unset — a realistic "staff hasn't got to this one yet" item.
    vatTreatment: null,
    name: "Presentation folder",
    catalogueId: "core-range",
    parts: ["folder"],
    productLabel: "Folders",
    icon: "folder",
    defaultQuantity: 500,
    quantityBreaks: [
      { qty: 100, price: 320 },
      { qty: 250, price: 520 },
      { qty: 500, price: 820 },
      { qty: 1000, price: 1400 },
      { qty: 2500, price: 2900 },
    ],
    artworkModes: ["upload", "later"],
    fields: [
      {
        key: "Print",
        label: "Print",
        mode: "choice",
        options: [
          o("4/0", "Full colour, outer", "4/0", true),
          o("4/4", "Both sides", "4/4"),
          o("5/0", "Colour + spot", "5/0"),
        ],
      },
      {
        key: "Board",
        label: "Board",
        mode: "fixed",
        fixedValue: "350–400gsm silk/matt",
      },
      {
        key: "Forme",
        label: "Die / forme",
        mode: "always_custom",
        control: "die",
        customHint:
          "Reuse a saved die, start from a template, or upload a die line",
      },
      {
        key: "Finishing",
        label: "Finishing",
        mode: "choice",
        control: "multi",
        options: [
          o("lam", "Lamination", "Laminating"),
          o("spotuv", "Spot UV", "Touch Enhancement"),
        ],
      },
    ],
  },
  {
    highlights: [],
    relatedItemIds: [],
    id: "standard-large-format",
    vatTreatment: "standard",
    name: "Large format panel",
    catalogueId: "core-range",
    parts: ["graphic"],
    productLabel: "Large Format — Panels",
    icon: "layout-board",
    defaultQuantity: 5,
    quantityBreaks: [
      { qty: 1, price: 38 },
      { qty: 2, price: 68 },
      { qty: 5, price: 150 },
      { qty: 10, price: 270 },
      { qty: 25, price: 590 },
    ],
    artworkModes: ["upload", "later"],
    fields: [
      {
        key: "Size",
        label: "Size",
        mode: "always_custom",
        control: "dimension",
        customValueType: "dimensions",
        // The mm limits are shown live from customConstraints below — this
        // hint says why the limit exists rather than repeating the numbers.
        customHint: "Limited to one substrate sheet — no seams",
        customConstraints: { max_width_mm: 1560, max_depth_mm: 3050 },
      },
      {
        key: "Substrate",
        label: "Substrate",
        mode: "choice_or_custom",
        options: [
          o("foamboard", "Foamboard 5mm", "rigid-foamboard", true),
          o("correx", "Correx 4mm", "rigid-correx"),
          o("dibond", "Dibond ACM", "rigid-dibond"),
        ],
      },
      {
        key: "Print",
        label: "Print",
        mode: "choice",
        options: [
          o("4/0", "Full colour, one side", "4/0", true),
          o("4/4", "Both sides", "4/4"),
        ],
      },
    ],
  },
  {
    highlights: [],
    relatedItemIds: [],
    id: "standard-ncr-set",
    vatTreatment: "standard",
    name: "NCR set",
    catalogueId: "core-range",
    parts: ["top", "middle", "bottom"],
    productLabel: "NCR Sets",
    icon: "copy",
    defaultQuantity: 2000,
    quantityBreaks: [
      { qty: 500, price: 180 },
      { qty: 1000, price: 240 },
      { qty: 2000, price: 360 },
      { qty: 5000, price: 720 },
      { qty: 10000, price: 1280 },
    ],
    artworkModes: ["on_file", "upload", "later"],
    fields: [
      {
        key: "Size",
        label: "Size",
        mode: "choice",
        options: [
          o("a5", "A5", "A5", true),
          o("a4", "A4", "A4"),
          o("dl", "DL", "210X99"),
        ],
      },
      {
        key: "Print",
        label: "Print",
        mode: "choice",
        options: [
          o("4/0", "Full colour", "4/0", true),
          o("1/0", "Mono", "1/0"),
          o("2pms/0", "Two spot colours", "2pms/0"),
        ],
      },
      {
        key: "SetSize",
        label: "Parts per set",
        mode: "choice",
        options: [
          o("2", "2-part", "2"),
          o("3", "3-part", "3", true),
          o("4", "4-part", "4"),
        ],
      },
      {
        key: "Numbering",
        label: "Numbering",
        mode: "choice",
        options: [
          o("none", "None", "none", true),
          o("seq", "Sequential", "sequential"),
        ],
      },
      {
        key: "Stock",
        label: "Paper",
        mode: "choice",
        options: [o("cb", "Carbonless white", "ncr-white-cb", true)],
      },
      {
        key: "StartNumber",
        label: "Start number",
        mode: "always_custom",
        customValueType: "text",
        customConstraints: { max_length: 12, digits_only: true },
        affectsCost: false,
        customHint:
          "The first number printed, for numbered work; leave blank when unnumbered",
      },
    ],
  },
  {
    highlights: [],
    relatedItemIds: [],
    id: "standard-perfect-bound-book",
    vatTreatment: "zero_rated",
    name: "Perfect Bound Book",
    catalogueId: "core-range",
    // Manufacturing surfaces review ("Read it as Henry" — bridging the mock
    // ID gap): dict-perfect-bound-book is cost-engine-validation.ts's live,
    // matches-throughout fixture (product type 36), with a full route/price
    // provenance breakdown for variant pbb-1 at quantity 250. There was no
    // catalogue item deriving from it at all, so the cost drilldown's route
    // provenance line had no live item to render non-null content for.
    // Field shape mirrors dictionary-admin.ts's own dict-perfect-bound-book
    // registration (cover stock choice + the Indigo print option) so the
    // two pages describe the same item.
    derivesFromDictionaryItemId: "dict-perfect-bound-book",
    parts: ["cover", "text", "insert"],
    productLabel: "Perfect Bound Books",
    icon: "book",
    defaultQuantity: 250,
    quantityBreaks: [
      { qty: 100, price: 482 },
      { qty: 250, price: 619 },
      { qty: 500, price: 894 },
    ],
    artworkModes: ["upload", "later"],
    fields: [
      {
        key: "cover",
        label: "Cover stock",
        mode: "choice",
        options: [
          o("300GSM_MATT", "300gsm matt lam", "300GSM_MATT", true),
          o("250GSM_GLOSS", "250gsm gloss lam", "250GSM_GLOSS"),
        ],
      },
      {
        key: "indigo",
        label: "Print on the HP Indigo",
        mode: "choice",
        customHint:
          "Routes this item to the HP Indigo 12000 when suitable; otherwise the cheapest suitable press.",
        options: [o("no", "No", "no", true), o("yes", "Yes", "yes")],
      },
    ],
  },
];

// ── Ramsay catalogue (customer) ─────────────────────────────────────────────

const ramsayItems: CatalogueItem[] = [
  {
    highlights: [],
    relatedItemIds: [],
    id: "ramsay-business-card",
    vatTreatment: "standard",
    name: "Business card",
    catalogueId: "ramsay",
    derivesFromCatalogueItemId: "standard-business-card",
    parts: ["sheet"],
    productLabel: "Business Cards",
    icon: "id-badge-2",
    defaultQuantity: 250,
    quantityBreaks: [
      { qty: 100, price: 28 },
      { qty: 250, price: 44 },
      { qty: 500, price: 66 },
      { qty: 1000, price: 100 },
    ],
    artworkModes: ["on_file", "online_editor", "upload"],
    fields: [
      { key: "Size", label: "Size", mode: "fixed", fixedValue: "90 × 55 mm" },
      {
        key: "Print",
        label: "Print",
        mode: "choice",
        options: [
          o("4/4", "Full colour, both sides", "4/4", true),
          o("4/0", "One side", "4/0"),
        ],
      },
      {
        key: "Board",
        label: "Board",
        mode: "fixed",
        fixedValue: "350gsm white (house)",
      },
    ],
  },
  {
    highlights: [],
    relatedItemIds: [],
    id: "ramsay-consent-form",
    vatTreatment: "standard",
    name: "Consent form 1",
    // A repeatable item (brandhub-order-routing 13): a fixed spec and one standing artwork, which expires.
    kind: "repeatable",
    artworkExpires: true,
    // Carried over by the customer catalogue migration (v1 completion F): the customer's own code, the press that
    // printed it last time, and the Tharstern jobs it was made from. Staff see all three on the item page.
    customerProductRef: "PCF-2",
    routeHint: {
      route: "make",
      costCentreCode: "ROLAND 700",
      lastJobNo: "738048",
      lastOn: "2026-09-02",
    },
    tharsternProvenance: {
      jobNos: ["738048", "731560", "726904"],
      lastJobNo: "738048",
    },
    catalogueId: "ramsay",
    parts: ["top", "middle", "bottom"],
    productLabel: "NCR Sets",
    icon: "clipboard-text",
    note: "Controlled document",
    specSummary: "A4 · 2-part NCR · mono · house bond",
    defaultQuantity: 100,
    quantityBreaks: [
      { qty: 50, price: 18 },
      { qty: 100, price: 32 },
      { qty: 250, price: 70 },
      { qty: 500, price: 130 },
    ],
    artworkModes: ["on_file"],
    // Numbered: the one field a repeatable NCR item leaves open, typed on every order.
    fields: [
      {
        key: "StartNumber",
        label: "Start number",
        mode: "always_custom",
        customValueType: "text",
        customConstraints: { max_length: 12, digits_only: true },
        affectsCost: false,
        customHint:
          "The first number printed, for numbered work; leave blank when unnumbered",
      },
    ],
  },
  {
    highlights: [],
    relatedItemIds: [],
    id: "ramsay-branded-pens",
    vatTreatment: "standard",
    name: "Branded pens",
    catalogueId: "ramsay",
    parts: ["goods"],
    productLabel: "Bought-in",
    icon: "pencil",
    specSummary: "Blue barrel · 1-colour logo",
    defaultQuantity: 250,
    quantityBreaks: [
      { qty: 100, price: 95 },
      { qty: 250, price: 190 },
      { qty: 500, price: 340 },
      { qty: 1000, price: 600 },
    ],
    artworkModes: [],
    fields: [],
  },
];

// ── SJP catalogue (punchout / variable-data) ────────────────────────────────

const sjpItems: CatalogueItem[] = [
  {
    highlights: [],
    relatedItemIds: [],
    id: "sjp-business-card",
    vatTreatment: "standard",
    name: "Business card",
    catalogueId: "perivan-sjp",
    parts: ["sheet"],
    productLabel: "API — Business Cards",
    icon: "id-badge-2",
    note: "Online stationery · variable data",
    specSummary: "85 × 55 mm · full colour both sides · 400gsm white",
    defaultQuantity: 250,
    quantityBreaks: [
      { qty: 250, price: 35 },
      { qty: 500, price: 52 },
      { qty: 1000, price: 84 },
    ],
    artworkModes: ["online_editor"],
    // The business card narrowed for SJP (catalogue-management-in-one mock-up): print and board fixed, so they read
    // as fixed; size and finishing stay the customer's choice.
    fields: [
      {
        key: "Size",
        label: "Size",
        mode: "choice",
        options: [
          o("85x55", "85 × 55 mm", "55X85", true),
          o("90x55", "90 × 55 mm", "55X90"),
        ],
      },
      {
        key: "Print",
        label: "Print",
        mode: "fixed",
        fixedValue: "Full colour, both sides",
      },
      {
        key: "Board",
        label: "Board",
        mode: "fixed",
        fixedValue: "400gsm white",
      },
      {
        key: "Finishing",
        label: "Finishing",
        mode: "choice",
        control: "multi",
        options: [
          o("lam", "Lamination", "Laminating", false, 8),
          o("spotuv", "Spot UV", "Touch Enhancement", false, 12),
          o("round", "Rounded corners", "Cut-Crease-Perf", false, 6),
        ],
      },
      {
        key: "Details",
        label: "Card details",
        mode: "always_custom",
        control: "variable_data",
        customHint: "Name, title, qualifications, phone, email",
      },
    ],
  },
  // SJP's letterhead and compliment slip, copied from Core range's, so the business card has a set to complete
  // (catalogue-management-in-one mock-up).
  ...["standard-letterhead", "standard-compliment-slip"].map((id) => ({
    ...standardItems.find((i) => i.id === id)!,
    id: id.replace("standard-", "sjp-"),
    catalogueId: "perivan-sjp",
  })),
];

const itemsByCatalogue: Record<string, CatalogueItem[]> = {
  "core-range": standardItems,
  ramsay: ramsayItems,
  "perivan-sjp": sjpItems,
};

const allItems: CatalogueItem[] = [
  ...standardItems,
  ...ramsayItems,
  ...sjpItems,
];

/**
 * Resolve an item's dictionary-rewire derivation for the mock adapter.
 *
 * Mock fixtures predate the split and model only `CatalogueItem`s — there is
 * no separate dictionary-item record. An item with an explicit
 * `derivesFromCatalogueItemId` narrows that parent (unchanged); every other
 * item is treated as a thin, unnarrowed derivation of a dictionary item that
 * shares its own id — "the core range is not special" (design doc),
 * so an item's own id doubling as its dictionary item id is exactly the
 * no-narrowing case the model describes. This is what lets
 * `getDictionaryItemDetail(item.id)` resolve for any fixture item that isn't
 * itself a narrowed derivation.
 */
function withDerivation(item: CatalogueItem): CatalogueItem {
  return {
    ...item,
    // A fixture's presentation values are what its author set, so they are its own values.
    ownIcon: item.ownIcon ?? item.icon,
    ownSpecSummary: item.ownSpecSummary ?? item.specSummary,
    ownDescription: item.ownDescription ?? item.description,
    derivesFromCatalogueItemId: item.derivesFromCatalogueItemId,
    derivesFromDictionaryItemId: item.derivesFromCatalogueItemId
      ? undefined
      : (item.derivesFromDictionaryItemId ?? item.id),
    narrowing: item.narrowing ?? {},
  };
}

export function getMockCatalogueScopes(): Catalogue[] {
  return mockCatalogues;
}

const addedCatalogueIds: string[] = [];
const addedItemIds: string[] = [];

/** *New catalogue…* in the mock: the catalogue exists, empty, for this session. */
export function addMockCatalogue(catalogue: Catalogue): void {
  mockCatalogues.push(catalogue);
  itemsByCatalogue[catalogue.id] = [];
  addedCatalogueIds.push(catalogue.id);
}

/** *New catalogue item…* in the mock: the item is in its catalogue, for this session. */
export function addMockCatalogueItem(item: CatalogueItem): void {
  itemsByCatalogue[item.catalogueId]?.push(item);
  allItems.push(item);
  addedItemIds.push(item.id);
}

/** Take away the catalogues and items the session's commands made (tests and stories). */
export function resetMockCatalogueAdditions(): void {
  for (const id of addedItemIds) {
    const inAll = allItems.findIndex((i) => i.id === id);
    if (inAll >= 0) allItems.splice(inAll, 1);
    for (const items of Object.values(itemsByCatalogue)) {
      const at = items.findIndex((i) => i.id === id);
      if (at >= 0) items.splice(at, 1);
    }
  }
  for (const id of addedCatalogueIds) {
    const at = mockCatalogues.findIndex((c) => c.id === id);
    if (at >= 0) mockCatalogues.splice(at, 1);
    delete itemsByCatalogue[id];
  }
  addedItemIds.length = 0;
  addedCatalogueIds.length = 0;
}

// ── Fulfilment-route narrowing (#87) ────────────────────────────────────────
//
// The catalogue-level narrowing lives on the `Catalogue` fixture itself, so a price preview reads it.

export function setMockCatalogueRouteNarrowing(
  catalogueId: string,
  routes: import("../data/supplier-pricing-types").RouteNarrowing,
): Catalogue | null {
  const catalogue = mockCatalogues.find((c) => c.id === catalogueId);
  if (!catalogue) return null;
  catalogue.routeNarrowing =
    routes.allowedRoutes?.length ||
    routes.pinnedRoute ||
    routes.allowedSpeeds != null
      ? routes
      : undefined;
  return catalogue;
}

// ── Cross-catalogue browse listing (mock twin of GET /catalogue/items) ──────

function catalogueNameFor(item: CatalogueItem): string {
  return mockCatalogues.find((c) => c.id === item.catalogueId)?.name ?? "";
}

function toTile(item: CatalogueItem): CatalogueItemTile {
  // Stands in for the real server-resolved cost-derived price (design doc
  // 48 / the manual-price-ladder retirement) — mock data has no cost cache
  // to derive from, so it reuses the item's own quantity-break rungs just to
  // produce a plausible number for stories/journeys.
  const indicativePricePence =
    item.quantityBreaks.length > 0
      ? Math.round(
          priceForQuantity(item.quantityBreaks, item.defaultQuantity).price *
            100,
        )
      : null;
  return {
    item: withDerivation(item),
    catalogueName: catalogueNameFor(item),
    needsSetup: needsSetup(item),
    fieldCount: item.fields.length,
    indicativePricePence,
  };
}

/**
 * Mock twin of the backend browse listing — identical filter/sort/search/page
 * semantics so pages behave the same against either adapter.
 * "created_at" is mocked as authoring order (later in the fixture = newer).
 */
export function listMockCatalogueItemTiles(
  options: CatalogueItemListOptions,
): ListResponse<CatalogueItemTile> {
  // Default order = the backend's "catalogue order": catalogues by name,
  // then each catalogue's curated item order.
  const cataloguesByName = mockCatalogues
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));
  let items = cataloguesByName.flatMap((c) => itemsByCatalogue[c.id] ?? []);

  if (options.catalogueIds && options.catalogueIds.length > 0) {
    const wanted = new Set(options.catalogueIds);
    items = items.filter((i) => wanted.has(i.catalogueId));
  }
  if (options.q) {
    const needle = options.q.toLowerCase();
    items = items.filter((i) =>
      [i.name, i.productLabel, i.specSummary ?? "", catalogueNameFor(i)].some(
        (h) => h.toLowerCase().includes(needle),
      ),
    );
  }

  if (options.sort) {
    const createdIndex = new Map(allItems.map((i, idx) => [i.id, idx]));
    const keyFns: Record<string, (i: CatalogueItem) => string | number> = {
      name: (i) => i.name,
      catalogue_name: (i) => catalogueNameFor(i),
      created_at: (i) => createdIndex.get(i.id) ?? -1,
    };
    const tokens = options.sort
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    items = [...items].sort((a, b) => a.id.localeCompare(b.id));
    for (const token of [...tokens].reverse()) {
      const desc = token.startsWith("-");
      const field = token.replace(/^[+-]/, "");
      const keyFn = keyFns[field];
      if (!keyFn) throw new Error(`unknown sort field: ${field}`);
      items = [...items].sort((a, b) => {
        const ka = keyFn(a);
        const kb = keyFn(b);
        const cmp = ka < kb ? -1 : ka > kb ? 1 : 0;
        return desc ? -cmp : cmp;
      });
    }
  }

  const page = options.page ?? 1;
  const pageSize = options.pageSize ?? 25;
  const total = items.length;
  const start = (page - 1) * pageSize;
  return {
    items: items.slice(start, start + pageSize).map(toTile),
    total,
    page,
    pageSize,
    totalPages: total === 0 ? 0 : Math.ceil(total / pageSize),
  };
}

export function getMockCatalogueView(
  catalogueId: string,
): CatalogueView | null {
  const catalogue = mockCatalogues.find((c) => c.id === catalogueId);
  if (!catalogue) return null;
  return {
    catalogue,
    items: (itemsByCatalogue[catalogueId] ?? []).map(withDerivation),
  };
}

export function getMockCatalogueItem(itemId: string): CatalogueItem | null {
  const item = allItems.find((i) => i.id === itemId);
  return item ? withDerivation(item) : null;
}

// ── The dictionary workbook download ───────────────────────────────────────
// What `GET /catalogue/workbook/export` answers: refusals as the route words them. The file is a stand-in: mock mode
// builds no real workbook (the backend does), so it opens as text, not in Excel.

export function mockDictionaryWorkbook(scope: DictionaryWorkbookScope): {
  blob: Blob;
  filename: string;
} {
  if (scope.catalogueIds.length === 0 && scope.wording === "none")
    throw workbookRefusal(
      "required",
      "Choose at least one catalogue, or a customer wording list.",
    );
  const known = new Set(mockCatalogues.map((c) => c.id));
  const unknown = scope.catalogueIds.find((id) => !known.has(id));
  if (unknown !== undefined)
    throw workbookRefusal("not_found", `One has no catalogue ${unknown}.`);
  const names = scope.catalogueIds.map(
    (id) => mockCatalogues.find((c) => c.id === id)!.name,
  );
  return {
    filename: "dictionary-and-catalogues.xlsx",
    blob: new Blob(
      [
        `Mock mode builds no workbook. It would hold: ${[...names, ...(scope.wording === "none" ? [] : [`customer wording (${scope.wording})`])].join(", ")}.`,
      ],
      {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      },
    ),
  };
}

function workbookRefusal(code: string, message: string): ApiError {
  const detail = {
    message: "Input validation failed with 1 error(s)",
    errors: [{ field: "catalogue_ids", code, message }],
  };
  // A file route's refusal reaches the adapter as the response text (`apiClient.getBlob` parses no body).
  return new ApiError(400, "Bad Request", JSON.stringify({ detail }), null);
}

// ── Variant-aware cost refresh (mock) ─────────────────────────────────────────
//
// Realistic multi-variant, multi-status fixtures for the catalogue editor's
// variant-matrix section — covers fresh, stale, and failed rows (the failed
// row keeps a last-known-good cost, matching the backend's retry-preserves-
// prior-cost behaviour) so every editor UI state is exercisable in Storybook
// and Playwright without a backend.

/** SJP's narrowed business card, costed at each size: its pinned print and board, its finishing priced on top. */
const sjpCardVariant = (
  size: "85x55" | "90x55",
  sortOrder: number,
): CatalogueItemVariantWithCosts => ({
  id: `var-sjp-bc-${size}`,
  catalogueItemId: "sjp-business-card",
  configuration: { Size: size },
  configurationHash: `hash-sjp-bc-${size}`,
  displayLabel: `${size === "85x55" ? "85 × 55" : "90 × 55"} mm / Full colour, both sides / 400gsm white`,
  isActive: true,
  sortOrder,
  quantityCosts: (
    [
      [250, 2480],
      [500, 3720],
      [1000, 5860],
    ] as const
  ).map(([quantity, costPence], i) => ({
    id: `vqc-sjp-bc-${size}-${quantity}`,
    variantId: `var-sjp-bc-${size}`,
    quantity,
    status: "fresh" as const,
    costPence: size === "85x55" ? costPence : costPence + 120 * (i + 1),
    weight: 0.4 * (quantity / 250),
    tharsternSellPricePence: null,
    purchasesPence: null,
    wagesPence: null,
    tharsternEstimateId: null,
    reference: null,
    payloadHash: null,
    schemaHash: null,
    staleReason: null,
    errorReason: null,
    refreshedAt: "2026-10-30T09:00:00Z",
    lastAttemptedAt: "2026-10-30T09:00:00Z",
    attemptCount: 1,
  })),
});

const mockVariantsByItem: Record<string, CatalogueItemVariantWithCosts[]> = {
  "sjp-business-card": [sjpCardVariant("85x55", 0), sjpCardVariant("90x55", 1)],
  "standard-business-card": [
    {
      id: "var-bc-1",
      catalogueItemId: "standard-business-card",
      configuration: { Size: "85x55", Print: "4/4", Board: "350" },
      configurationHash: "hash-bc-1",
      displayLabel: "85 × 55 mm / Full colour, both sides / 350gsm white",
      isActive: true,
      sortOrder: 0,
      quantityCosts: [
        {
          id: "vqc-bc-1-250",
          variantId: "var-bc-1",
          quantity: 250,
          status: "fresh",
          costPence: 2140,
          weight: 0.4,
          tharsternSellPricePence: 4800,
          purchasesPence: 1177,
          wagesPence: 642,
          tharsternEstimateId: 458213,
          reference: "E-458213",
          payloadHash: "6f2a9c1e...",
          schemaHash: "b8d4e072...",
          staleReason: null,
          errorReason: null,
          refreshedAt: "2026-07-28T09:12:00Z",
          lastAttemptedAt: "2026-07-28T09:12:00Z",
          attemptCount: 1,
        },
        {
          id: "vqc-bc-1-500",
          variantId: "var-bc-1",
          quantity: 500,
          status: "fresh",
          costPence: 3260,
          weight: 0.8,
          tharsternSellPricePence: 7200,
          purchasesPence: 1793,
          wagesPence: 978,
          tharsternEstimateId: 458214,
          reference: "E-458214",
          payloadHash: "1a3f7b02...",
          schemaHash: "b8d4e072...",
          staleReason: null,
          errorReason: null,
          refreshedAt: "2026-07-28T09:12:04Z",
          lastAttemptedAt: "2026-07-28T09:12:04Z",
          attemptCount: 1,
        },
      ],
    },
    {
      id: "var-bc-2",
      catalogueItemId: "standard-business-card",
      configuration: { Size: "85x55", Print: "4/0", Board: "350" },
      configurationHash: "hash-bc-2",
      displayLabel: "85 × 55 mm / One side / 350gsm white",
      isActive: true,
      sortOrder: 1,
      quantityCosts: [
        {
          id: "vqc-bc-2-250",
          variantId: "var-bc-2",
          quantity: 250,
          status: "stale",
          costPence: 1980,
          weight: 0.4,
          tharsternSellPricePence: 4400,
          purchasesPence: 1089,
          wagesPence: 594,
          tharsternEstimateId: 441098,
          reference: "E-441098",
          payloadHash: "9e0c4d18...",
          schemaHash: "a771f930...",
          staleReason: "Tharstern schema changed since this cost was cached",
          errorReason: null,
          refreshedAt: "2026-06-02T14:30:00Z",
          lastAttemptedAt: "2026-06-02T14:30:00Z",
          attemptCount: 1,
        },
        {
          // A break in the current ladder that has never been costed yet —
          // surfaced as a refreshable pending cell (backend synthesises these).
          id: "pending:var-bc-2:500",
          variantId: "var-bc-2",
          quantity: 500,
          status: "pending",
          costPence: null,
          weight: null,
          tharsternSellPricePence: null,
          purchasesPence: null,
          wagesPence: null,
          tharsternEstimateId: null,
          reference: null,
          payloadHash: null,
          schemaHash: null,
          staleReason: null,
          errorReason: null,
          refreshedAt: null,
          lastAttemptedAt: null,
          attemptCount: 0,
        },
      ],
    },
    {
      id: "var-bc-3",
      catalogueItemId: "standard-business-card",
      configuration: { Size: "90x55", Print: "4/4", Board: "400" },
      configurationHash: "hash-bc-3",
      displayLabel: "90 × 55 mm / Full colour, both sides / 400gsm white",
      isActive: true,
      sortOrder: 2,
      quantityCosts: [
        {
          id: "vqc-bc-3-250",
          variantId: "var-bc-3",
          quantity: 250,
          status: "failed",
          // Last known-good cost is preserved across a failed retry.
          costPence: 2280,
          weight: 0.45,
          tharsternSellPricePence: 5100,
          purchasesPence: 1254,
          wagesPence: 684,
          tharsternEstimateId: 452207,
          reference: "E-452207",
          payloadHash: "77bb21f4...",
          schemaHash: "b8d4e072...",
          staleReason: null,
          errorReason:
            "EstimateApiError: Tharstern rejected the submitted paper criteria (no matching stock)",
          refreshedAt: "2026-07-10T11:05:00Z",
          lastAttemptedAt: "2026-07-29T16:40:00Z",
          attemptCount: 3,
        },
      ],
    },
  ],
  // Bridges the mock ID gap ("Read it as Henry", cost-engine-productionisation
  // review): variant id and quantity match cost-engine-validation.ts's
  // dict-brochure-cover-text fixture exactly (variant "bct-1", lineTemplates
  // key "bct-1:500") so the catalogue item page's route/price provenance
  // has a real, paused item to render for.
  "standard-brochure": [
    {
      id: "bct-1",
      catalogueItemId: "standard-brochure",
      configuration: {
        Size: "a4",
        Pages: "16",
        CoverPrint: "4/4",
        CoverBoard: "silk",
        TextPrint: "4/4",
        TextStock: "silk",
      },
      configurationHash: "hash-bct-1",
      displayLabel: "Cover 250gsm silk + Text 130gsm, 4pp + 16pp self-cover",
      isActive: true,
      sortOrder: 0,
      quantityCosts: [
        {
          id: "vqc-bct-1-500",
          variantId: "bct-1",
          quantity: 500,
          status: "fresh",
          // Matches the dict-brochure-cover-text fixture's bct-1/500 cell
          // exactly (mismatching — 5351 · Ink; total updated in coordinator
          // review round 4 to include the paper and saddle-stitch lines the
          // pair was missing) so the two pages never disagree about this
          // item's cost.
          costPence: 75300,
          weight: 1.4,
          tharsternSellPricePence: 160000,
          purchasesPence: 34900,
          wagesPence: 18600,
          tharsternEstimateId: 471822,
          reference: "E-471822",
          payloadHash: "3c8e5f61...",
          schemaHash: "b8d4e072...",
          staleReason: null,
          errorReason: null,
          refreshedAt: "2026-09-10T02:00:00Z",
          lastAttemptedAt: "2026-09-10T02:00:00Z",
          attemptCount: 1,
        },
      ],
    },
  ],
  // Bridges the mock ID gap: variant id and quantity match
  // cost-engine-validation.ts's dict-perfect-bound-book fixture exactly
  // (variant "pbb-1", lineTemplates key "pbb-1:250") — the live counterpart
  // to standard-brochure's paused example above.
  "standard-perfect-bound-book": [
    {
      id: "pbb-1",
      catalogueItemId: "standard-perfect-bound-book",
      configuration: { cover: "300GSM_MATT" },
      configurationHash: "hash-pbb-1",
      displayLabel: "A5 214pp, 300gsm cover matt lam, 90gsm text",
      isActive: true,
      sortOrder: 0,
      quantityCosts: [
        {
          id: "vqc-pbb-1-250",
          variantId: "pbb-1",
          quantity: 250,
          status: "fresh",
          // Matches the dict-perfect-bound-book fixture's pbb-1/250 cell
          // exactly (green — matches throughout, including its perfect-bind
          // finishing line added in coordinator review round 3 and its
          // paper lines added in round 4).
          costPence: 45800,
          weight: 0.9,
          tharsternSellPricePence: 91600,
          purchasesPence: 19200,
          wagesPence: 10100,
          tharsternEstimateId: 468390,
          reference: "E-468390",
          payloadHash: "af12d7e0...",
          schemaHash: "b8d4e072...",
          staleReason: null,
          errorReason: null,
          refreshedAt: "2026-09-11T10:31:00Z",
          lastAttemptedAt: "2026-09-11T10:31:00Z",
          attemptCount: 1,
        },
      ],
    },
  ],
};

export function getMockVariantsForItem(
  itemId: string,
): CatalogueItemVariantWithCosts[] {
  return mockVariantsByItem[itemId] ?? [];
}

// ── Cost lineage fixtures (the drill-down panel's read path) ────────────────
//
// Shapes mirror docs/features/product-catalogue/44_method_fields_probe.md's
// real captured TEST estimate (field names, key presence/absence, and the
// realistic-looking markup violation) so mock and real adapters exercise the
// same panel logic. Keyed by the `VariantQuantityCost.id`s above so each of
// the matrix's meaningful states — fresh/zero-markup, fresh/markup-violated,
// stale/no-lineage, failed/payload-only — has a matching lineage fixture.
const mockCostLineageByCostId: Record<
  string,
  {
    submittedPayload: Record<string, unknown>;
    estimateResponse: Record<string, unknown> | null;
  }
> = {
  // vqc-bc-1-250 — fresh, zero markup (the invariant holding as intended).
  "vqc-bc-1-250": {
    submittedPayload: {
      ProductTypeID: 5,
      Quantity: 250,
      FinishedSize: { Code: "85X55" },
      Customer: { Code: "ONE-CATALOGUE", Name: "One Catalogue" },
      IgnoreShipping: true,
      Parts: [
        {
          Name: "Card",
          PaperCode: "WWW_EDIXOFFSET_350",
          GSM: 350,
          FrontProcess: 4,
          BackProcess: 4,
          Processes: [],
        },
      ],
    },
    estimateResponse: {
      ID: 458213,
      EstimateRef: "E-458213",
      Weight: 0.4,
      Price: 21.4,
      Costs: {
        Paper: 11.77,
        OriginationMaterial: 0.0,
        OtherMaterial: 0.0,
        Outwork: 0.0,
        Carriage: 0.0,
        PrePress: 0.0,
        Printing: 6.42,
        Finishing: 0.0,
        OrderExpense: 3.21,
        TotalCost: 21.4,
      },
      Markups: { TotalMarkupPercent: 0.0, TotalMarkupValue: 0.0 },
      Sheets: [
        {
          ID: 1001,
          SheetNo: 1,
          Dimensions: { Code: "450X320", Width: 450.0, Depth: 320.0 },
        },
      ],
      Parts: [
        {
          StockDimensions: { Code: "450X320", Width: 450.0, Depth: 320.0 },
          Processes: [
            {
              Type: { Value: 8 },
              ProcessTypeID: 24,
              CostCentreID: 5830,
              TotalDurationMinutes: 4,
            },
            { Type: { Value: 13 }, ProcessTypeID: 36, CostCentreID: 801 },
          ],
        },
      ],
    },
  },
  // vqc-bc-1-500 — fresh, markup NOT zero: Tharstern's own default commercial
  // schedule still applies to the house customer (design doc invariant 3's
  // tripwire, mirroring the real 20%/Price≠TotalCost the probe captured).
  "vqc-bc-1-500": {
    submittedPayload: {
      ProductTypeID: 5,
      Quantity: 500,
      FinishedSize: { Code: "85X55" },
      Customer: { Code: "ONE-CATALOGUE", Name: "One Catalogue" },
      IgnoreShipping: true,
      Parts: [
        {
          Name: "Card",
          PaperCode: "WWW_EDIXOFFSET_350",
          GSM: 350,
          FrontProcess: 4,
          BackProcess: 4,
          Processes: [],
        },
      ],
    },
    estimateResponse: {
      ID: 458214,
      EstimateRef: "E-458214",
      Weight: 0.8,
      Price: 39.12,
      Costs: {
        Paper: 15.53,
        OriginationMaterial: 0.0,
        OtherMaterial: 0.0,
        Outwork: 0.0,
        Carriage: 0.0,
        PrePress: 0.0,
        Printing: 12.83,
        Finishing: 0.0,
        OrderExpense: 4.24,
        TotalCost: 32.6,
      },
      Markups: { TotalMarkupPercent: 20.0, TotalMarkupValue: 6.52 },
      Sheets: [
        {
          ID: 1002,
          SheetNo: 1,
          Dimensions: { Code: "450X320", Width: 450.0, Depth: 320.0 },
        },
      ],
      Parts: [
        {
          StockDimensions: { Code: "450X320", Width: 450.0, Depth: 320.0 },
          Processes: [
            {
              Type: { Value: 8 },
              ProcessTypeID: 24,
              CostCentreID: 5830,
              TotalDurationMinutes: 8,
            },
            { Type: { Value: 13 }, ProcessTypeID: 36, CostCentreID: 801 },
          ],
        },
      ],
    },
  },
  // vqc-bc-3-250 — failed retry: the rejected payload survives (a primary
  // debugging surface per the design doc), no response was ever received.
  "vqc-bc-3-250": {
    submittedPayload: {
      ProductTypeID: 5,
      Quantity: 250,
      FinishedSize: { Code: "90X55" },
      Customer: { Code: "ONE-CATALOGUE", Name: "One Catalogue" },
      IgnoreShipping: true,
      Parts: [
        {
          Name: "Card",
          PaperCode: "WWW_VISIONSUPERIOR_400",
          GSM: 400,
          FrontProcess: 4,
          BackProcess: 4,
          Processes: [],
        },
      ],
    },
    estimateResponse: null,
  },
  // vqc-bc-2-250 deliberately has no entry: it models a row that predates
  // lineage capture shipping — the matrix's "no-lineage" degradation state.
};

/** `null` means no lineage captured for this cost row — the mock adapter
 * surfaces that as a 404, same as the real one. */
export function getMockCostLineage(costId: string): {
  submittedPayload: Record<string, unknown>;
  estimateResponse: Record<string, unknown> | null;
} | null {
  return mockCostLineageByCostId[costId] ?? null;
}

const mockCostRefreshRuns = new Map<string, CatalogueCostRefreshRun>();
let mockCostRefreshRunCounter = 0;

export function createMockCostRefreshRun(
  itemId: string,
  scope: { variantId?: string; quantity?: number },
): CatalogueCostRefreshRun {
  mockCostRefreshRunCounter += 1;
  const runId = `run-mock-${mockCostRefreshRunCounter}`;
  const variants = getMockVariantsForItem(itemId);
  const totalPairs = scope.variantId
    ? 1
    : variants.reduce((sum, v) => sum + v.quantityCosts.length, 0) || 1;
  const run: CatalogueCostRefreshRun = {
    runId,
    catalogueItemId: itemId,
    variantId: scope.variantId ?? null,
    quantity: scope.quantity ?? null,
    status: "RUNNING",
    triggerSource: "manual",
    startedAt: new Date().toISOString(),
    completedAt: null,
    totalPairs,
    succeededCount: 0,
    failedCount: 0,
    skippedCount: 0,
    failureReason: null,
  };
  mockCostRefreshRuns.set(runId, run);
  return run;
}

/** Mock runs complete on their first poll — there's no real async work to wait for. */
export function pollMockCostRefreshRun(
  runId: string,
): CatalogueCostRefreshRun | null {
  const run = mockCostRefreshRuns.get(runId);
  if (!run) return null;
  if (run.status === "RUNNING") {
    const completed: CatalogueCostRefreshRun = {
      ...run,
      status: "COMPLETE",
      completedAt: new Date().toISOString(),
      succeededCount: run.totalPairs,
    };
    mockCostRefreshRuns.set(runId, completed);
    return completed;
  }
  return run;
}

/**
 * The in-house route's Speeds at the preview's price: what a preview offers
 * before route options are applied (#90). Its tiers share one price, so the
 * faster tiers fall into Standard and the slower into Economy, as the real
 * time file's do.
 */
function mockInHouseSpeeds(
  quotedPence: number,
  delivery: CatalogueDelivery,
  req: MockSpeedRequest,
): SpeedChoice {
  const optionDelivery =
    delivery.status === "priced" && delivery.deliveryPricePence != null
      ? {
          kind: "priced" as const,
          deliveryPricePence: delivery.deliveryPricePence,
        }
      : delivery.status === "collection" || delivery.status === "included"
        ? { kind: "none" as const }
        : { kind: "at_address" as const };
  return mockSpeedChoice(
    mockInHouseOptions(quotedPence, optionDelivery, req.neededBy),
    req,
  ).speeds;
}

/** The mock's sell price for a cached cost — Tharstern's own figure, else cost × 2. */
function mockQuotedPenceFor(cost: VariantQuantityCost): number {
  return cost.tharsternSellPricePence ?? (cost.costPence ?? 0) * 2;
}

// ── On-demand custom-value costing (design doc "2b"/"2c") ───────────────────
//
// The fixture variants above cover the preset configurations; a custom value
// (a bespoke Size, a page count outside the seeded presets…) has no fixture
// to match, mirroring the real backend's on-demand minting: nothing is
// cached until `costCatalogueConfiguration` is actually asked for it. This
// cache holds what has been "asked for" in this mock session, keyed by
// (item, configuration, quantity) rather than by variant id — the mock has
// no variant-minting step of its own to key against.

function stableConfigKey(configuration: Record<string, string>): string {
  return Object.entries(configuration)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([k, v]) => `${k}=${v}`)
    .join("&");
}

function onDemandCostKey(
  itemId: string,
  configuration: Record<string, string>,
  quantity: number,
): string {
  return `${itemId}::${stableConfigKey(configuration)}::${quantity}`;
}

const mockOnDemandCosts = new Map<string, VariantQuantityCost>();
let mockOnDemandCostCounter = 0;

/**
 * A plausible manufacturing cost for a configuration with no fixture —
 * deliberately simple (fixed fee + a per-unit rate), not a model of any
 * real product's economics. What matters for exercising the ladder UX is
 * that it's deterministic per (item, configuration, quantity) and roughly
 * near-linear in quantity, echoing the real Tharstern model the design
 * doc's probe found (`docs/features/product-catalogue/`).
 */
function syntheticCostPence(itemId: string, quantity: number): number {
  const fixedFeePence = 1200;
  const perUnitPence = itemId.includes("large-format") ? 900 : 18;
  return fixedFeePence + quantity * perUnitPence;
}

/**
 * Mock counterpart of `costCatalogueConfiguration` — mints/live-costs a
 * (configuration, quantity) pair on demand, on the first request; a second
 * identical request reuses the cached row, exactly like the real endpoint.
 * `delay()` in `mock-adapter.ts` already simulates the network round trip;
 * this returns synchronously once called.
 */
export function mockCostCatalogueConfiguration(
  itemId: string,
  configuration: Record<string, string>,
  quantity: number,
): VariantQuantityCost {
  const key = onDemandCostKey(itemId, configuration, quantity);
  const existing = mockOnDemandCosts.get(key);
  if (existing) return existing;

  mockOnDemandCostCounter += 1;
  const costPence = syntheticCostPence(itemId, quantity);
  const cost: VariantQuantityCost = {
    id: `vqc-on-demand-${mockOnDemandCostCounter}`,
    variantId: `var-on-demand-${stableConfigKey(configuration)}`,
    quantity,
    status: "fresh",
    costPence,
    weight: Math.round(quantity * 0.004 * 100) / 100,
    tharsternSellPricePence: null,
    purchasesPence: Math.round(costPence * 0.4),
    wagesPence: Math.round(costPence * 0.6),
    tharsternEstimateId: 900_000 + mockOnDemandCostCounter,
    reference: `E-${900_000 + mockOnDemandCostCounter}`,
    payloadHash: null,
    schemaHash: null,
    staleReason: null,
    errorReason: null,
    refreshedAt: new Date().toISOString(),
    lastAttemptedAt: new Date().toISOString(),
    attemptCount: 1,
  };
  mockOnDemandCosts.set(key, cost);
  return cost;
}

/** The on-demand cache's cost for this exact (item, configuration, quantity),
 *  if it has been warmed — checked ahead of the fixture-variant lookup so a
 *  custom value that has been costed once prices from here on. */
function onDemandCostFor(
  itemId: string,
  configuration: Record<string, string>,
  quantity: number,
): VariantQuantityCost | undefined {
  return mockOnDemandCosts.get(
    onDemandCostKey(itemId, configuration, quantity),
  );
}

/**
 * Live price preview for the internal/CSR ordering UI. Matches the
 * requested configuration against the fixture variants above so every
 * cost-lineage state (fresh, stale, failed-with-retained-cost, no variant
 * match) is exercisable from mock data without a backend.
 *
 * Covers the whole quantity ladder as the real endpoint does. The fixture
 * variants deliberately hold costs for only some of each item's quantity
 * breaks, so the mock exercises a part-costed ladder — the state that used to
 * render list prices as though they were quotes.
 */
// The MVP's honest inactive factors, exactly as resolve_price emits them.
const MOCK_INACTIVE_FACTOR_LINES: QuoteLine[] = [
  {
    label: "Market intelligence",
    kind: "add",
    source: "market",
    amount: 0,
    applied: false,
    note: "Not active in MVP",
  },
  {
    label: "Capacity pricing",
    kind: "add",
    source: "capacity",
    amount: 0,
    applied: false,
    note: "Not active in MVP",
  },
];

/** The work style a mock item's spec resolves to — what int_est__work_style
 *  would say. Business cards and stationery are digital; brochures litho. */
function mockWorkStyleForItem(itemId: string): string {
  const item = getMockCatalogueItem(itemId);
  const label = (item?.productLabel ?? item?.name ?? "").toLowerCase();
  if (/brochure|book|leaflet/.test(label)) return "litho";
  if (/format|banner|panel/.test(label)) return "wide_format";
  if (/pen|merch/.test(label)) return "merch";
  return "digital";
}

export function createMockPricePreview(
  itemId: string,
  req: {
    configuration: Record<string, string>;
    quantity: number;
    customerCode?: string | null;
    delivery?: CatalogueDeliverySelection | null;
    /** False prices the selected quantity only — see `mockPriceLadder`. */
    ladder?: boolean;
  } & MockSpeedRequest,
): CatalogueItemPricePreview {
  const variants = getMockVariantsForItem(itemId);
  const variant = variants.find((v) =>
    Object.entries(v.configuration).every(
      ([key, value]) => req.configuration[key] === value,
    ),
  );
  // An on-demand-warmed custom configuration has no fixture variant to match
  // — check the warm cache first, exactly the precedence the real backend's
  // variant-then-cost lookup gives an on-demand row once it exists.
  const cost =
    onDemandCostFor(itemId, req.configuration, req.quantity) ??
    variant?.quantityCosts.find((c) => c.quantity === req.quantity);
  // No customer chosen yet resolves against the central-scope price group — the
  // manufacturing cost is keyed on variant × quantity, not on the customer.
  const priceGroupName = req.customerCode
    ? `Standard trade — ${req.customerCode}`
    : "Standard trade";
  const costedPrecedent = mockPrecedentForCataloguePreview(
    req.customerCode,
    req.quantity,
  );

  // Provenance ids for the panel's deep links, mirroring the backend: the
  // price group is whatever the customer's assignment resolves to (central
  // "standard" otherwise), and the dictionary item is the one the catalogue
  // item derives from — its own id for an unnarrowed fixture item.
  const priceGroupId =
    getMockAssignments().find((a) => a.customerCode === req.customerCode)
      ?.priceGroupId ?? "standard";
  const dictionaryItemId =
    getMockCatalogueItem(itemId)?.derivesFromDictionaryItemId ?? itemId;

  // Cost drilldown / price provenance (design note "Amendments to existing
  // surfaces"): echo the resolved cost's own per-cost-centre reasons and
  // chosen press(es) rather than re-deriving them.
  // `formatCostDrilldownLine` words them; the mock never authors a
  // second, independently-worded summary.
  const priceCosting = variant
    ? getMockPriceCosting(dictionaryItemId, variant.id, req.quantity)
    : null;
  const chosenCostCentreIds = [
    ...new Set(
      (priceCosting?.pressesConsidered ?? [])
        .filter((p) => p.outcome === "chosen")
        .map((p) => Number(p.pressCode.split(" ")[0])),
    ),
  ];
  const drilldownPresses: CostDrilldownPress[] = chosenCostCentreIds
    .map((costCentreId) => {
      const reason = priceCosting?.costCentreReasons[costCentreId];
      if (!reason?.partSpecLabel) return null;
      return {
        costCentreId,
        partName: reason.partSpecLabel.split(" · ")[0],
        code: reason.code,
      };
    })
    .filter((p): p is CostDrilldownPress => p !== null);
  const drilldownPolicyVersion = chosenCostCentreIds
    .map((id) => priceCosting?.costCentreReasons[id]?.policyVersion)
    .find((v): v is number => v != null);
  const costDrilldownLine =
    drilldownPresses.length > 0 && drilldownPolicyVersion != null
      ? formatCostDrilldownLine(drilldownPresses, drilldownPolicyVersion)
      : null;
  const priceProvenance: PriceProvenance = {
    source: "cis_engine",
    costedAt: COSTED_AT,
    checksPassed: priceCosting
      ? priceCosting.state !== "red" && priceCosting.state !== "grey"
      : true,
  };

  const ladder = mockPriceLadder(
    itemId,
    req.configuration,
    variant,
    req.quantity,
    req.ladder ?? true,
  );

  const delivery = mockPriceDelivery(itemId, req.delivery);
  const destinationDeliveries = mockPriceDestinations(itemId, req.delivery);

  if (!cost || cost.costPence == null) {
    // No manufacturing-cost snapshot for this configuration/quantity —
    // fall back to the manual quantity-break price, same as the backend.
    const perUnitPence = 20;
    const quotedPence = req.quantity * perUnitPence;
    const uncostedPrecedent = mockPrecedentForCataloguePreview(
      req.customerCode,
      req.quantity,
    );
    return {
      quotedPence,
      perUnitPence,
      verdictStatus: "ok",
      priceGroupName,
      priceGroupId,
      dictionaryItemId,
      lines: [
        {
          label: "Quantity-break price",
          kind: "base",
          source: "base",
          amount: quotedPence / 100,
        },
        ...MOCK_INACTIVE_FACTOR_LINES,
      ],
      speeds: mockInHouseSpeeds(quotedPence, delivery, req),
      baseSource: "quantity_break",
      costSnapshotId: null,
      variantId: variant?.id ?? null,
      costStatus: null,
      costStaleReason: null,
      costRefreshedAt: null,
      tharsternEstimateRef: null,
      weight: null,
      payloadHash: null,
      verdictReason: null,
      contributionMarginPct: null,
      concessionPct: 0,
      manufacturingCostPence: null,
      purchasesPence: null,
      wagesPence: null,
      tharsternSellPricePence: null,
      quantityLadder: ladder.map((row) => ({
        ...row,
        priceLevels: mockPriceLevels({
          precedent: uncostedPrecedent,
          quantity: row.quantity,
          policyPence:
            row.quotedPence ?? Math.round(row.quantity * perUnitPence),
          costPence: null,
          workStyle: mockWorkStyleForItem(itemId),
        }),
      })),
      precedent: uncostedPrecedent,
      priceLevels: mockPriceLevels({
        precedent: uncostedPrecedent,
        quantity: req.quantity,
        policyPence: quotedPence,
        costPence: null,
        workStyle: mockWorkStyleForItem(itemId),
      }),
      costDrilldownLine: null,
      priceProvenance: null,
      delivery,
      destinationDeliveries,
      totalWithDeliveryPence: mockTotalWithDelivery(quotedPence, delivery),
      // The mock prices every route's delivery it offers (doc 107: none is withheld here).
      deliveryNotPricedWarnings: [],
      isDeliveryUnpriced: false,
    };
  }

  const quotedPence = mockQuotedPenceFor(cost);
  return {
    quotedPence,
    perUnitPence: Math.ceil(quotedPence / req.quantity),
    verdictStatus: "ok",
    priceGroupName,
    priceGroupId,
    dictionaryItemId,
    // The same line shapes the cost-visible backend produces: the base split
    // into cost + base margin, the cost's bought-in/wages split as inert
    // sub-rows, and the honest inactive factor stubs.
    lines: [
      {
        label: "Manufacturing cost",
        kind: "base",
        source: "cost",
        amount: cost.costPence / 100,
      },
      ...(cost.purchasesPence != null
        ? [
            {
              label: "of which bought-in materials",
              kind: "add",
              source: "cost_purchases",
              amount: cost.purchasesPence / 100,
              applied: false,
            } satisfies QuoteLine,
          ]
        : []),
      ...(cost.wagesPence != null
        ? [
            {
              label: "of which production wages",
              kind: "add",
              source: "cost_wages",
              amount: cost.wagesPence / 100,
              applied: false,
            } satisfies QuoteLine,
          ]
        : []),
      {
        label: "Base margin",
        kind: "add",
        source: "base_margin",
        amount: (quotedPence - cost.costPence) / 100,
        note: `${Math.round(((quotedPence - cost.costPence) / quotedPence) * 100)}%`,
      },
      ...MOCK_INACTIVE_FACTOR_LINES,
    ],
    speeds: mockInHouseSpeeds(quotedPence, delivery, req),
    baseSource: "manufacturing_cost",
    costSnapshotId: cost.id,
    variantId: variant?.id ?? null,
    costStatus: cost.status,
    costStaleReason: cost.staleReason,
    costRefreshedAt: cost.refreshedAt,
    tharsternEstimateRef: cost.reference,
    weight: cost.weight,
    payloadHash: cost.payloadHash,
    verdictReason: null,
    contributionMarginPct:
      Math.round(((quotedPence - cost.costPence) / quotedPence) * 1000) / 10,
    concessionPct: 0,
    manufacturingCostPence: cost.costPence,
    purchasesPence: cost.purchasesPence ?? null,
    wagesPence: cost.wagesPence ?? null,
    tharsternSellPricePence: cost.tharsternSellPricePence,
    quantityLadder: ladder.map((row) => ({
      ...row,
      priceLevels:
        row.quotedPence == null
          ? undefined
          : mockPriceLevels({
              precedent: costedPrecedent,
              quantity: row.quantity,
              policyPence: row.quotedPence,
              costPence:
                (
                  onDemandCostFor(itemId, req.configuration, row.quantity) ??
                  variant?.quantityCosts.find(
                    (c) => c.quantity === row.quantity,
                  )
                )?.costPence ?? null,
              workStyle: mockWorkStyleForItem(itemId),
            }),
    })),
    precedent: costedPrecedent,
    priceLevels: mockPriceLevels({
      precedent: costedPrecedent,
      quantity: req.quantity,
      policyPence: quotedPence,
      costPence: cost.costPence,
      workStyle: mockWorkStyleForItem(itemId),
    }),
    costDrilldownLine,
    priceProvenance,
    delivery,
    destinationDeliveries,
    totalWithDeliveryPence: mockTotalWithDelivery(quotedPence, delivery),
    // The mock prices every route's delivery it offers (doc 107: none is withheld here).
    deliveryNotPricedWarnings: [],
    isDeliveryUnpriced: false,
  };
}

/**
 * The item's quantity breaks priced for one variant — same rules as the
 * backend's ladder: only a cached cost yields a price, savings run against the
 * smallest priced quantity, and a quantity with no cost carries no number.
 *
 * `isCompleteLadder=false` mirrors the backend's `ladder=False`: only the
 * selected quantity is priced, and only when it is a standard break at all (a
 * typed custom quantity was never a ladder row). Savings and best value are
 * then left unanswered, because both compare across the whole ladder — see
 * `_build_quantity_ladder` in
 * `catalogue_module/actions/preview_catalogue_item_price.py`.
 */
function mockPriceLadder(
  itemId: string,
  configuration: Record<string, string>,
  variant: CatalogueItemVariantWithCosts | undefined,
  selectedQuantity: number,
  isCompleteLadder: boolean = true,
): CataloguePriceLadderRow[] {
  const item = getMockCatalogueItem(itemId);
  const quantities = [...(item?.quantityBreaks ?? [])]
    .map((b) => b.qty)
    .sort((a, b) => a - b)
    .filter((qty) => isCompleteLadder || qty === selectedQuantity);

  const costFor = (qty: number): VariantQuantityCost | undefined =>
    onDemandCostFor(itemId, configuration, qty) ??
    variant?.quantityCosts.find((c) => c.quantity === qty);

  const priced = new Map<number, { quotedPence: number; perUnit: number }>();
  for (const qty of quantities) {
    const cost = costFor(qty);
    if (!cost || cost.costPence == null || qty <= 0) continue;
    const quotedPence = mockQuotedPenceFor(cost);
    priced.set(qty, { quotedPence, perUnit: quotedPence / qty });
  }
  const perUnits = [...priced.values()].map((p) => p.perUnit);
  const baselinePerUnit = perUnits[0] ?? 0;
  const bestPerUnit = perUnits.length > 0 ? Math.min(...perUnits) : 0;

  return quantities.map((qty) => {
    const row = priced.get(qty);
    const cost = costFor(qty);
    if (!row) {
      return {
        quantity: qty,
        availability: "not_costed" as const,
        quotedPence: null,
        savingPct: null,
        isBestValue: false,
        isSelected: qty === selectedQuantity,
        costStatus: cost?.status ?? null,
      };
    }
    return {
      quantity: qty,
      availability: "priced" as const,
      quotedPence: row.quotedPence,
      savingPct: !isCompleteLadder
        ? null
        : baselinePerUnit > 0
          ? Math.round((1 - row.perUnit / baselinePerUnit) * 100)
          : 0,
      isBestValue: isCompleteLadder && row.perUnit === bestPerUnit,
      isSelected: qty === selectedQuantity,
      costStatus: cost?.status ?? null,
    };
  });
}
